import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useRef, type CSSProperties } from 'react';
import { BrandMark, MarkShapes } from './BrandMark';
import { MARK_CENTER, RING_SIGNAL_PATH, WORDMARK, type MarkTone } from './geometry';
import { DURATION, EASE, clamp01, lerp, range } from '../motion/tokens';
import { prefersReducedMotion } from '../motion/preferences';
import './brand.css';

/**
 * "COLESCOMEX no aparece. COLESCOMEX se integra."
 *
 * The isotipo is sliced into radial sectors (clip paths over the exact shapes). Sectors start
 * displaced in depth and rotation, report as nodes, get coordinated by a hairline network,
 * converge radially and lock. At rest the sectors are replaced by the untouched static
 * BrandMark, so the final frame is the exact identity — never an approximation.
 *
 * progress 0 → 1:  FRAGMENTED · NODES · COORDINATION · CONVERGENCE · RECONSTRUCTION · INTEGRATED
 */
export type AssemblyState = 'STATIC' | 'FRAGMENTED' | 'INTEGRATING' | 'INTEGRATED' | 'PULSE' | 'LOOP_AMBIENT';
export type AssemblyPreset = 'ASSEMBLE' | 'DISASSEMBLE' | 'FRAGMENT_TO_INTEGRATE' | 'SIGNAL_PULSE' | 'AMBIENT_ALIVE';
export interface BrandAssemblyHandle {
  /** Scrub the assembly (scroll-driven). Cancels any running preset. */
  setProgress(progress: number): void;
  play(preset: AssemblyPreset): Promise<void>;
  stop(): void;
  getState(): AssemblyState;
}

const SECTORS = 14;
const LETTERS = (WORDMARK.primary + WORDMARK.secondary).split('');
/** Deterministic per-sector scatter (no Math.random: identical on every visit and SSR-safe). */
const scatter = Array.from({ length: SECTORS }, (_, index) => {
  const h = (n: number) => { const x = Math.sin((index + 1) * 12.9898 * n) * 43758.5453; return x - Math.floor(x); };
  const mid = ((index + 0.5) / SECTORS) * Math.PI * 2;
  return {
    mid, radial: 30 + h(1) * 34, depth: -260 + h(2) * 380, spin: (h(3) - 0.5) * 42, tiltX: (h(4) - 0.5) * 70, tiltY: (h(5) - 0.5) * 70,
    lag: h(6) * 0.22,
  };
});
const sectorClip = (index: number) => {
  const a0 = (index / SECTORS) * Math.PI * 2 - 0.004; const a1 = ((index + 1) / SECTORS) * Math.PI * 2 + 0.004; const r = 150;
  const p = (a: number) => `${(MARK_CENTER.x + Math.cos(a) * r).toFixed(2)} ${(MARK_CENTER.y + Math.sin(a) * r).toFixed(2)}`;
  return `M${MARK_CENTER.x} ${MARK_CENTER.y} L${p(a0)} L${p((a0 + a1) / 2)} L${p(a1)} Z`;
};

export const BrandAssembly = forwardRef<BrandAssemblyHandle, {
  tone?: MarkTone; className?: string; label?: string; initial?: number; showWordmark?: boolean; autoPlay?: AssemblyPreset | null;
}>(function BrandAssembly({ tone = 'dark', className = '', label = 'COLESCOMEX', initial = 0, showWordmark = true, autoPlay = null }, forwarded) {
  const idPrefix = `cxa${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const root = useRef<HTMLDivElement>(null);
  const sectors = useRef<(HTMLDivElement | null)[]>([]);
  const letters = useRef<(HTMLSpanElement | null)[]>([]);
  const network = useRef<SVGSVGElement>(null);
  const signal = useRef<SVGPathElement>(null);
  const state = useRef<AssemblyState>('STATIC');
  const progress = useRef(initial);
  const animation = useRef(0);
  const resolveRun = useRef<(() => void) | null>(null);

  const apply = useCallback((raw: number) => {
    const t = clamp01(raw); progress.current = t;
    const element = root.current; if (!element) return;
    const integrated = t >= 0.985;
    element.dataset.integrated = String(integrated);
    state.current = t <= 0.001 ? 'FRAGMENTED' : integrated ? 'INTEGRATED' : 'INTEGRATING';
    const width = element.clientWidth || 1; const unit = width / 200;
    const nodes: [number, number, number][] = [];
    scatter.forEach((s, index) => {
      const layer = sectors.current[index]; if (!layer) return;
      // Each sector runs the same sequence with a small deterministic lag: align (rotation/depth) then converge (radial).
      const local = clamp01((t - s.lag * 0.6) / (1 - s.lag * 0.6));
      const align = EASE.integrate(range(local, 0.08, 0.55));
      const converge = EASE.settle(range(local, 0.42, 0.92));
      const radial = s.radial * (1 - converge);
      const x = Math.cos(s.mid) * radial * unit; const y = Math.sin(s.mid) * radial * unit;
      const z = lerp(s.depth, 0, align);
      layer.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, ${z.toFixed(1)}px) rotateZ(${(s.spin * (1 - align)).toFixed(2)}deg) rotateX(${(s.tiltX * (1 - align)).toFixed(2)}deg) rotateY(${(s.tiltY * (1 - align)).toFixed(2)}deg)`;
      layer.style.opacity = (0.28 + 0.72 * EASE.standard(range(local, 0, 0.5))).toFixed(3);
      // Node anchor at the sector's mid-radius, in mark units (2D projection of depth by perspective ≈ scale).
      const perspective = 900 / (900 - z); const nodeRadius = 74 + radial;
      nodes.push([100 + Math.cos(s.mid) * nodeRadius * perspective, 100 + Math.sin(s.mid) * nodeRadius * perspective, local]);
    });
    // Coordination network: nodes appear, link to neighbours (the chain), then dissolve into the locked mark.
    const svg = network.current;
    if (svg) {
      const appear = range(t, 0.04, 0.26); const vanish = 1 - range(t, 0.7, 0.9);
      svg.style.opacity = (appear * vanish).toFixed(3);
      const links = svg.querySelectorAll<SVGLineElement>('line'); const dots = svg.querySelectorAll<SVGRectElement>('rect');
      nodes.forEach(([nx, ny], index) => {
        const dot = dots[index]; if (dot) { dot.setAttribute('x', (nx - 1.6).toFixed(2)); dot.setAttribute('y', (ny - 1.6).toFixed(2)); }
        const next = nodes[(index + 1) % nodes.length]; const link = links[index];
        if (link && next) {
          const linked = range(t, 0.18 + index * 0.012, 0.42 + index * 0.012);
          link.setAttribute('x1', nx.toFixed(2)); link.setAttribute('y1', ny.toFixed(2));
          link.setAttribute('x2', lerp(nx, next[0], linked).toFixed(2)); link.setAttribute('y2', lerp(ny, next[1], linked).toFixed(2));
        }
      });
    }
    letters.current.forEach((letter, index) => {
      if (!letter) return;
      const local = EASE.settle(range(t, 0.6 + index * 0.018, 0.86 + index * 0.012));
      letter.style.opacity = local.toFixed(3);
      letter.style.transform = `translate3d(0, ${((1 - local) * 0.42).toFixed(3)}em, 0)`;
    });
    element.style.setProperty('--presence', EASE.standard(range(t, 0, 0.1)).toFixed(3));
    element.style.setProperty('--descriptor', EASE.standard(range(t, 0.84, 1)).toFixed(3));
  }, []);

  const stop = useCallback(() => {
    cancelAnimationFrame(animation.current); animation.current = 0;
    resolveRun.current?.(); resolveRun.current = null;
    root.current?.removeAttribute('data-pulse');
  }, []);

  const run = useCallback((from: number, to: number, duration: number) => new Promise<void>(resolve => {
    stop(); resolveRun.current = resolve;
    const start = performance.now();
    const tick = (now: number) => {
      const k = clamp01((now - start) / duration);
      apply(lerp(from, to, k));
      if (k < 1) animation.current = requestAnimationFrame(tick);
      else { animation.current = 0; resolveRun.current = null; resolve(); }
    };
    animation.current = requestAnimationFrame(tick);
  }), [apply, stop]);

  const pulse = useCallback((loop: boolean) => {
    const element = root.current; const path = signal.current; if (!element || !path) return Promise.resolve();
    stop(); apply(1);
    state.current = loop ? 'LOOP_AMBIENT' : 'PULSE'; element.dataset.pulse = loop ? 'ambient' : 'once';
    if (loop) return Promise.resolve();
    return new Promise<void>(resolve => {
      const done = () => { path.removeEventListener('animationend', done); if (state.current === 'PULSE') { element.removeAttribute('data-pulse'); state.current = 'INTEGRATED'; } resolve(); };
      path.addEventListener('animationend', done);
    });
  }, [apply, stop]);

  useImperativeHandle(forwarded, () => ({
    setProgress(value: number) { if (animation.current) stop(); apply(prefersReducedMotion() ? 1 : value); },
    async play(preset: AssemblyPreset) {
      if (prefersReducedMotion()) { stop(); apply(preset === 'DISASSEMBLE' ? 1 : 1); return; }
      if (preset === 'ASSEMBLE') return run(progress.current >= 0.985 ? 0 : progress.current, 1, DURATION.integrate * 1.6);
      if (preset === 'DISASSEMBLE') return run(progress.current, 0, DURATION.integrate);
      if (preset === 'FRAGMENT_TO_INTEGRATE') { apply(0); await new Promise(resolve => setTimeout(resolve, DURATION.settle)); return run(0, 1, DURATION.integrate * 2); }
      if (preset === 'SIGNAL_PULSE') return pulse(false);
      return pulse(true);
    },
    stop,
    getState: () => state.current,
  }), [apply, pulse, run, stop]);

  useEffect(() => {
    apply(prefersReducedMotion() ? 1 : initial);
    const element = root.current; if (!element) return;
    const resize = new ResizeObserver(() => apply(progress.current)); resize.observe(element);
    return () => { resize.disconnect(); stop(); };
  }, [apply, initial, stop]);

  useEffect(() => {
    if (!autoPlay || prefersReducedMotion()) return;
    const element = root.current; if (!element) return;
    let started = false;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || started) return; started = true; observer.disconnect();
      const handle = { run, pulse };
      if (autoPlay === 'ASSEMBLE' || autoPlay === 'FRAGMENT_TO_INTEGRATE') void handle.run(0, 1, DURATION.integrate * 1.8).then(() => handle.pulse(false));
      else if (autoPlay === 'SIGNAL_PULSE') void handle.pulse(false);
      else if (autoPlay === 'AMBIENT_ALIVE') void handle.pulse(true);
    }, { threshold: 0.35 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [autoPlay, pulse, run]);

  return (
    <div ref={root} className={`brand-assembly brand-assembly--${tone} ${className}`.trim()} {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      style={{ '--descriptor': initial >= 0.985 ? 1 : 0 } as CSSProperties}>
      <div className="brand-assembly__stage" aria-hidden="true">
        <svg className="brand-assembly__defs" width="0" height="0" focusable="false">
          <defs>
            <g id={`${idPrefix}-mark`}><MarkShapes tone={tone} idPrefix={`${idPrefix}g`} /></g>
            {scatter.map((_, index) => <clipPath key={index} id={`${idPrefix}-s${index}`}><path d={sectorClip(index)} /></clipPath>)}
          </defs>
        </svg>
        {scatter.map((_, index) => (
          <div key={index} ref={node => { sectors.current[index] = node; }} className="brand-assembly__sector">
            <svg viewBox="0 0 200 200"><g clipPath={`url(#${idPrefix}-s${index})`}><use href={`#${idPrefix}-mark`} /></g></svg>
          </div>
        ))}
        <svg ref={network} className="brand-assembly__network" viewBox="0 0 200 200">
          {scatter.map((_, index) => <line key={`l${index}`} />)}
          {scatter.map((_, index) => <rect key={`d${index}`} width="3.2" height="3.2" />)}
        </svg>
        <div className="brand-assembly__exact"><BrandMark layout="mark" tone={tone} label={null} /></div>
        <svg className="brand-assembly__signal" viewBox="0 0 200 200">
          <path ref={signal} d={RING_SIGNAL_PATH} pathLength={1} />
        </svg>
      </div>
      {showWordmark && (
        <div className="brand-assembly__type" aria-hidden="true">
          <span className="brand-assembly__word">
            {LETTERS.map((letter, index) => (
              <span key={index} ref={node => { letters.current[index] = node; }} className={index >= WORDMARK.primary.length ? 'is-secondary' : undefined}>{letter}</span>
            ))}
          </span>
          <span className="brand-assembly__descriptor">{WORDMARK.descriptor}</span>
        </div>
      )}
    </div>
  );
});
